'use client';

import { useEffect } from 'react';
import { Monitor, Globe, Trash2, LogOut } from 'lucide-react';
import { useSessions } from '@/hooks/useSessions';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { formatDateTime } from '@/lib/utils';

export function SessionsPage() {
  const { sessions, loading, fetchSessions, revokeSession, revokeAllSessions } = useSessions();

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  const handleRevoke = async (sessionId: string) => {
    await revokeSession(sessionId);
  };

  const handleRevokeAll = async () => {
    await revokeAllSessions();
    window.location.href = '/login';
  };

  const getDeviceIcon = () => {
    return <Monitor className="h-5 w-5 text-cyan-400" />;
  };

  const getDeviceType = () => 'Desktop';
  const getBrowser = () => 'Unknown';
  const getOS = () => 'Unknown';

  if (loading && sessions.length === 0) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-10 w-32" />
        </div>
        {[1, 2].map(i => (
          <Card key={i} className="glass">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <Skeleton className="h-12 w-12 rounded-lg" />
                  <div>
                    <Skeleton className="h-5 w-32 mb-2" />
                    <Skeleton className="h-4 w-48" />
                  </div>
                </div>
                <Skeleton className="h-4 w-20" />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">Active Sessions</h1>
          <p className="text-slate-400 mt-1">Manage your active sessions and devices</p>
        </div>
        {sessions.length > 1 && (
          <Dialog>
            <DialogTrigger asChild>
              <Button variant="destructive">
                <LogOut className="mr-2 h-4 w-4" />
                Sign Out Everywhere
              </Button>
            </DialogTrigger>
            <DialogContent className="glass max-w-md">
              <DialogHeader>
                <DialogTitle>Sign out from all devices?</DialogTitle>
                <DialogDescription>
                  This will sign you out from all devices and sessions. You'll need to log in again on each device.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button variant="outline" autoFocus>Cancel</Button>
                <Button
                  variant="destructive"
                  onClick={handleRevokeAll}
                >
                  Sign Out Everywhere
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {sessions.length === 0 ? (
        <Card className="glass">
          <CardContent className="pt-6 text-center py-12">
            <Globe className="h-12 w-12 text-slate-600 mx-auto mb-4" />
            <p className="text-slate-400">No active sessions found</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {sessions.map((session) => (
            <Card key={session.id} className="glass">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-slate-800/50 rounded-xl">
                      {getDeviceIcon()}
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <p className="font-medium text-white">
                          {getDeviceType()} • {getBrowser()} on {getOS()}
                        </p>
                        {session.isCurrent && (
                          <Badge className="bg-green-500/10 text-green-400">Current</Badge>
                        )}
                      </div>
                      <p className="text-sm text-slate-400 flex items-center gap-1">
                        <Globe className="h-3 w-3" />
                        {session.ip || 'Unknown IP'}
                      </p>
                      <p className="text-xs text-slate-500 mt-1">
                        Last active: {formatDateTime(session.createdAt)}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <Badge variant="secondary">Active</Badge>
                    {!session.isCurrent && (
                      <Dialog>
                        <DialogTrigger asChild>
                          <Button variant="ghost" size="sm" className="text-red-400 hover:text-red-300">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="glass max-w-md">
                          <DialogHeader>
                            <DialogTitle>Revoke this session?</DialogTitle>
                            <DialogDescription>
                              This will immediately sign out this session. The device will no longer have access until you log in again.
                            </DialogDescription>
                          </DialogHeader>
                          <DialogFooter>
                            <Button variant="outline" autoFocus>Cancel</Button>
                            <Button
                              variant="destructive"
                              onClick={() => handleRevoke(session.id)}
                            >
                              Revoke Session
                            </Button>
                          </DialogFooter>
                        </DialogContent>
                      </Dialog>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
